import {Components, styled, Theme} from '@mui/material/styles';
import {SliderThumb, SliderValueLabel, SliderOwnerState} from '@mui/material/Slider';
import {Children, isValidElement, PropsWithChildren, useLayoutEffect, useRef} from 'react';
import {sliderClasses} from '@mui/material';
import Typography from '@mui/material/Typography';
import {isString} from 'lodash/fp';

const SValueLabel = styled(Typography)(() => ({
  position: 'absolute',
  transform: 'translateX(-50%) translateY(calc(100% + 10px))',
}));

const BigAlwaysDisplayedValueLabel = (
  props: PropsWithChildren<{
    ownerState: SliderOwnerState;
    value: string;
  }>
) => {
  const ref = useRef<HTMLDivElement>(null);
  const children = useRef(props.children);
  children.current = props.children;

  const calcLeft = () => {
    if (!ref.current) {
      return;
    }

    const slider = ref.current?.parentElement;

    if (!slider) {
      return;
    }

    const labelRect = ref.current?.getBoundingClientRect();
    const sliderRect = slider?.getBoundingClientRect();

    let left = 0;

    Children.forEach(children.current, child => {
      if (!isValidElement(child)) {
        return;
      }

      const {style} = child.props as {style?: {left?: string}};

      if (style && isString(style.left)) {
        left = parseFloat(style.left);
      }
    });

    const leftPx = ((sliderRect?.width || 0) / 100) * left;
    const isRightEdge = leftPx + labelRect.width / 2 > sliderRect.width;

    ref.current.style.left = isRightEdge
      ? `${sliderRect.width - labelRect.width / 2}px`
      : `${Math.max(leftPx, labelRect.width / 2)}px`;
  };

  useLayoutEffect(() => {
    calcLeft();
    window.addEventListener('resize', calcLeft);

    return () => {
      window.removeEventListener('resize', calcLeft);
    };
  }, [props.value]);

  return (
    <>
      {props.children}
      <SValueLabel noWrap ref={ref} variant='subtitle3' color='text.primary'>
        {props.value}
      </SValueLabel>
    </>
  );
};

const SliderValueLabelSlot = (
  props: PropsWithChildren<{
    ownerState: SliderOwnerState;
    value: string;
  }>
) => {
  if (props.ownerState.size !== 'big' && props.ownerState.valueLabelDisplay !== 'on') {
    return <SliderValueLabel {...(props as any)} />;
  }

  return <BigAlwaysDisplayedValueLabel {...(props as any)} />;
};

const SliderThumbSlot = (
  props: PropsWithChildren<{
    ownerState: SliderOwnerState;
    style: {
      left?: string;
    };
  }>
) => {
  if (props.ownerState.size === 'big') {
    const {children, ...other} = props;
    return (
      <SliderThumb {...other}>
        {children}
        <span className='bar' />
        <span className='bar' />
        <span className='bar' />
      </SliderThumb>
    );
  }

  return <SliderThumb {...props} />;
};

export const slider = (theme: Theme): Pick<Components, 'MuiSlider'> => ({
  MuiSlider: {
    defaultProps: {
      slots: {
        thumb: SliderThumbSlot,
        valueLabel: SliderValueLabelSlot,
      },
    },
    variants: [
      {
        props: {size: 'big'},
        style: {
          borderRadius: theme.shape.borderRadius,

          [`&:not(.${sliderClasses.vertical})`]: {
            height: 16,
          },
          [`&.${sliderClasses.vertical}`]: {
            width: 16,
          },

          [`& .${sliderClasses.rail}`]: {
            backgroundColor: theme.palette.background.darkGrey,
          },
          [`& .${sliderClasses.track}`]: {
            backgroundColor: theme.palette.background.darkGrey,
            borderColor: 'transparent',
          },

          '& .MuiSlider-thumbSizeBig': {
            '&::before': {
              display: 'none',
            },

            borderRadius: theme.shape.borderRadiusSmall,
            backgroundColor: theme.palette.dataTypes.accounts[50],

            '&:hover, &.': {
              boxShadow: '0px 0px 0px 6px rgba(0, 0, 197, 0.16)',
            },
            '&.Mui-active': {
              boxShadow: '0px 0px 0px 8px rgba(0, 0, 197, 0.16)',
            },

            '& .bar': {
              height: 11,
              width: 1,
              backgroundColor: theme.palette.common.white,
              marginLeft: 2,
              marginRight: 2,
            },
          },
        },
      },
      {
        props: {
          size: 'big',
          valueLabelDisplay: 'on',
        },
        style: {
          [`& .${sliderClasses.valueLabel}.${sliderClasses.valueLabelOpen}`]: {
            display: 'none',
          },
        },
      },
    ],
  },
});
